import type { DocumentVisibility, Profile, StaffDocument } from "@amazing-care/types";
import {
  AbilityBuilder,
  createMongoAbility,
  type ForcedSubject,
  type MongoAbility,
  subject,
} from "@casl/ability";

/**
 * What the UI may offer, derived from the caller's profile. With SAML the
 * profile's role and departments come from Entra groups on every sign-in, so
 * this follows group changes without any UI work.
 *
 * These rules mirror the RLS policies (amazing-care-backend docs/database.md, "Access matrix").
 * The database is what enforces access; this only decides what to show, so a
 * rule here that is looser than RLS shows a button that then fails, and one
 * that is stricter hides something the caller may do. Keep both in step, and
 * extend ability.test.ts alongside the backend's pgTAP tests when either changes.
 */

export type Action = "read" | "create" | "update" | "delete" | "manage";

interface DocumentFields {
  departmentId: string;
  visibility: DocumentVisibility;
}

type DocumentSubject = DocumentFields & ForcedSubject<"Document">;

export type Subject =
  | "all"
  | "Document"
  | DocumentSubject
  | "Announcement"
  | "Activity"
  /** The links on the home page; admins edit them. */
  | "QuickLink"
  /** Manager accounts and their departments, i.e. user management. */
  | "Manager"
  /** The upload and delete history on the home page. */
  | "ActivityLog";

export type AppAbility = MongoAbility<[Action, Subject]>;

type AbilityProfile = Pick<Profile, "role" | "managedDepartmentIds">;

const VISIBLE_TO_ANY_MANAGER: DocumentVisibility[] = ["everyone", "managers"];
const SETTABLE_BY_MANAGERS: DocumentVisibility[] = ["everyone", "managers", "department_managers"];

export function defineAbilityFor(profile: AbilityProfile | null): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  // No profile means not a member: RLS returns nothing, so offer nothing.
  if (!profile) return build();

  if (profile.role === "Admin") {
    can("manage", "all");
    return build();
  }

  can("read", ["Announcement", "Activity", "QuickLink"]);

  if (profile.role === "Staff") {
    can("read", "Document", { visibility: "everyone" });
    return build();
  }

  const managed = profile.managedDepartmentIds;
  can("read", "Document", { visibility: { $in: VISIBLE_TO_ANY_MANAGER } });
  can("read", "Document", { visibility: "department_managers", departmentId: { $in: managed } });
  // "Admins only" is excluded: a manager would lock themselves out of it.
  can(["create", "update", "delete"], "Document", {
    departmentId: { $in: managed },
    visibility: { $in: SETTABLE_BY_MANAGERS },
  });
  can("read", "ActivityLog");

  return build();
}

/**
 * A document as a permission subject. Pass the visibility it would have, for
 * a document that does not exist yet or is about to change.
 */
export function asDocument(
  document: Pick<StaffDocument, "departmentId" | "visibility">
): DocumentSubject {
  return subject("Document", {
    departmentId: document.departmentId,
    visibility: document.visibility ?? "everyone",
  });
}
